import type { AuthState } from './auth.svelte';
import type {
  Customer, GuardDutyAcknowledgementEvent, GuardDutyFinding, GuardDutyUpdate, Incident, IncidentEvent, OverviewCustomer, Problem,
  SeverityBand, SuppressionAudit, SuppressionRule, Topic,
} from './types';

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

export interface TimeBoundPage<T> extends Page<T> {
  from: string;
  to: string;
}

export interface TimeBounds {
  from: string;
  to: string;
}

export class ApiClient {
  constructor(private readonly auth: AuthState) {}

  overview(): Promise<{
    generatedAt: string;
    customers: OverviewCustomer[];
    guardDuty: { from: string; minimumSeverity: SeverityBand; items: GuardDutyFinding[] };
  }> {
    return this.get('/api/v1/overview');
  }

  incidents(query = ''): Promise<TimeBoundPage<Incident>> {
    return this.get(`/api/v1/incidents${query}`);
  }

  incident(id: string, cursor = ''): Promise<{ summary: Incident; events: IncidentEvent[]; nextCursor: string | null }> {
    const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return this.get(`/api/v1/incidents/${encodeURIComponent(id)}${query}`);
  }

  customers(): Promise<{ items: Customer[] }> {
    return this.get('/api/v1/customers');
  }

  customer(id: string): Promise<Customer> {
    return this.get(`/api/v1/customers/${encodeURIComponent(id)}`);
  }

  customerIncidents(id: string, cursor = '', bounds?: TimeBounds): Promise<TimeBoundPage<Incident>> {
    const query = new URLSearchParams();
    if (cursor) query.set('cursor', cursor);
    if (bounds) {
      query.set('from', bounds.from);
      query.set('to', bounds.to);
    }
    const suffix = query.size ? `?${query}` : '';
    return this.get(`/api/v1/customers/${encodeURIComponent(id)}/incidents${suffix}`);
  }

  customerTopics(id: string): Promise<{ items: Topic[] }> {
    return this.get(`/api/v1/customers/${encodeURIComponent(id)}/topics`);
  }

  guardDutyFindings(query = ''): Promise<TimeBoundPage<GuardDutyFinding>> {
    return this.get(`/api/v1/guardduty/findings${query}`);
  }

  guardDutyFinding(id: string, cursor = ''): Promise<{
    summary: GuardDutyFinding;
    events: GuardDutyUpdate[];
    nextCursor: string | null;
  }> {
    const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return this.get(`/api/v1/guardduty/findings/${encodeURIComponent(id)}${query}`);
  }

  customerGuardDutyFindings(id: string, cursor = '', bounds?: TimeBounds): Promise<TimeBoundPage<GuardDutyFinding>> {
    const query = new URLSearchParams({ minimumSeverity: 'medium' });
    if (cursor) query.set('cursor', cursor);
    if (bounds) {
      query.set('from', bounds.from);
      query.set('to', bounds.to);
    }
    return this.get(`/api/v1/customers/${encodeURIComponent(id)}/guardduty/findings?${query}`);
  }

  async acknowledgeGuardDutyFinding(id: string): Promise<void> {
    await this.request(`/api/v1/guardduty/findings/${encodeURIComponent(id)}/acknowledgement`, { method: 'PUT' });
  }

  async restoreGuardDutyFinding(id: string): Promise<void> {
    await this.request(`/api/v1/guardduty/findings/${encodeURIComponent(id)}/acknowledgement`, { method: 'DELETE' });
  }

  guardDutyAcknowledgementEvents(id: string, cursor = ''): Promise<Page<GuardDutyAcknowledgementEvent>> {
    const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return this.get(`/api/v1/guardduty/findings/${encodeURIComponent(id)}/acknowledgement-events${query}`);
  }

  suppressionRules(cursor = ''): Promise<Page<SuppressionRule>> {
    const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return this.get(`/api/v1/guardduty/suppressions${query}`);
  }

  suppressionAudit(cursor = ''): Promise<Page<SuppressionAudit>> {
    const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return this.get(`/api/v1/guardduty/suppressions/audit${query}`);
  }

  createSuppression(input: {
    sourceFindingId: string;
    scope: 'global' | 'customer';
    qualifiers: Array<'severity' | 'account' | 'resource'>;
    reason: string;
  }): Promise<SuppressionRule> {
    return this.request('/api/v1/guardduty/suppressions', { method: 'POST', body: JSON.stringify(input) });
  }

  async deleteSuppression(id: string): Promise<void> {
    await this.request(`/api/v1/guardduty/suppressions/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  private async get<T>(path: string): Promise<T> {
    return this.request(path);
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await this.auth.accessToken();
    const headers = new Headers(init.headers);
    headers.set('Authorization', `Bearer ${token}`);
    if (init.body) headers.set('Content-Type', 'application/json');
    const response = await fetch(path, { ...init, headers });
    if (response.status === 401 || response.status === 403) {
      await this.auth.expire();
      throw new Error('Your session has expired');
    }
    if (!response.ok) {
      let problem: Problem | null = null;
      try {
        problem = (await response.json()) as Problem;
      } catch {
        // API Gateway authentication failures do not use the application problem format.
      }
      throw new Error(problem?.detail ?? `Request failed with status ${response.status}`);
    }
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }
}
